import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import JsonLd, { getWebPageSchema, getBreadcrumbSchema } from '@/components/feature/JsonLd';
import { categoryList } from '@/mocks/home';
import { comicsByCategory, categoryPageDescriptions } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();

  const currentCategory = categoryList.find((c) => c.slug === slug) || categoryList[0];
  const description = categoryPageDescriptions[slug] || categoryPageDescriptions.all;
  const comics = comicsByCategory[slug] || comicsByCategory.all;

  const otherCategories = categoryList.filter((c) => c.slug !== slug);

  const pageUrl = `https://aimanhua.com/category/${slug}`;
  const pageTitle = `${description.title} - 爱漫画网在线漫画阅读`;

  return (
    <>
      <JsonLd data={getWebPageSchema(pageTitle, description.desc, pageUrl)} />
      <JsonLd data={getBreadcrumbSchema([
        { name: '首页', url: 'https://aimanhua.com' },
        { name: description.title, url: pageUrl },
      ])} />
      <Layout>
        {/* ... rest of the component stays the same ... */}
        <div className="pt-16">
          {/* Breadcrumb */}
          <div className="bg-background-50 border-b border-background-200/60">
            <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-3">
              <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
                <span className="text-foreground-800 font-medium">{description.title}</span>
              </nav>
            </div>
          </div>

          {/* Category Header */}
          <div className="bg-background-50 border-b border-background-200/60">
            <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-8">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-2">{description.title}</h1>
              <p className="text-sm text-foreground-600 max-w-2xl">{description.desc}</p>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="bg-background-50 border-b border-background-200/60 sticky top-16 z-20">
            <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
                {categoryList.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 cursor-pointer ${
                      slug === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Comic Grid */}
          <div className="py-10 md:py-14">
            <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
              {comics.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {comics.map((comic) => (
                    <Link
                      key={comic.id}
                      to={`/article/${comic.title === '斗破苍穹' ? 'doupo-cangqiong' : comic.title === '一人之下' ? 'yirenzhixia' : comic.title === '镖人' ? 'biao-ren-review' : 'manhua-trend-2026'}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
                        <img
                          src={comic.cover}
                          alt={comic.title}
                          title={`${comic.title} - ${comic.category}漫画`}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                          <span className="text-white text-xs bg-primary-500 px-2 py-1 rounded-md">{comic.status}</span>
                        </div>
                        <div className="absolute top-2 right-2 bg-black/60 text-accent-400 text-[10px] px-1.5 py-0.5 rounded flex items-center gap-0.5">
                          <i className="ri-star-fill text-[8px]"></i> {comic.rating}
                        </div>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors truncate">{comic.title}</h3>
                      <div className="flex items-center gap-2 mt-1 text-xs text-foreground-500">
                        <span>{comic.author}</span>
                        <span className="text-foreground-300">|</span>
                        <span>{comic.views} 阅读</span>
                      </div>
                      <p className="text-xs text-foreground-400 mt-0.5 truncate">{comic.updateTo}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {comic.tags.slice(0, 2).map((tag) => (
                          <span key={tag} className="text-[10px] bg-secondary-100 text-secondary-700 px-1.5 py-0.5 rounded whitespace-nowrap">{tag}</span>
                        ))}
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 bg-background-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">该分类暂无漫画内容</p>
                  <Link to="/category/all" className="text-primary-500 hover:text-primary-600 text-sm mt-2 inline-block">
                    查看全部漫画
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Other Categories */}
          {otherCategories.length > 0 && (
            <div className="py-10 md:py-14 bg-background-100">
              <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
                <h2 className="text-lg font-heading text-foreground-950 mb-5">探索更多分类</h2>
                <div className="flex flex-wrap gap-2">
                  {otherCategories.slice(0, 8).map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-1.5 px-4 py-2 bg-background-50 border border-background-200/60 rounded-full text-sm text-foreground-600 hover:text-primary-500 hover:border-primary-200 transition-all whitespace-nowrap cursor-pointer"
                    >
                      <i className={`${cat.icon} text-sm`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </Layout>
    </>
  );
}